import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useUser } from '../contexts/UserContext'
import { motion, AnimatePresence } from 'framer-motion'
import Footer from '../components/Footer'
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  Clock,
  CheckCircle2,
  Circle,
  AlertCircle,
  User,
  Users,
  Calendar,
  Tag,
  Trash2,
  Edit,
  Eye,
  X,
  ChevronDown,
  Menu,
  LayoutGrid,
  Wifi,
  WifiOff
} from 'lucide-react'
import { TableSkeleton } from '../components/LoadingSpinner'

export default function TaskManagement() {
  const { userInfo, loading: userLoading } = useUser()
  const navigate = useNavigate()

  // State management
  const [tasks, setTasks] = useState([])
  const [filteredTasks, setFilteredTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterPriority, setFilterPriority] = useState('all')
  const [viewMode, setViewMode] = useState('board') // 'board' or 'list'
  const [showFilters, setShowFilters] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [activeTab, setActiveTab] = useState('comments') // comments, workflow, history, activity
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [taskHistory, setTaskHistory] = useState([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [realtimeConnected, setRealtimeConnected] = useState(false)
  const fetchTasksTimeoutRef = React.useRef(null)

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    project_code: '',
    status: 'todo',
    priority: 'medium',
    due_date: '',
    assigned_users: [], // Changed to array for multi-select
    tags: ''
  })

  // Teams state
  const [teams, setTeams] = useState([])
  const [selectedTeam, setSelectedTeam] = useState('all')
  const [teamMembers, setTeamMembers] = useState([])
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [showMembersModal, setShowMembersModal] = useState(false)
  const [managingTeam, setManagingTeam] = useState(null)
  const [availableUsers, setAvailableUsers] = useState([])
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  })

  // Multi-team assignment state
  const [selectedTeams, setSelectedTeams] = useState([]) // Array of team IDs yang dipilih untuk assign
  const [allTeamMembers, setAllTeamMembers] = useState({}) // Object: {teamId: [members]}
  const [selectedUsersByTeam, setSelectedUsersByTeam] = useState({}) // Object: {teamId: [userIds]}

  // Documents state
  const [taskDocuments, setTaskDocuments] = useState([])
  const [uploadingDocument, setUploadingDocument] = useState(false)
  
  // Delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [taskToDelete, setTaskToDelete] = useState(null)

  useEffect(() => {
    if (!userLoading && !userInfo) {
      navigate('/login')
    }
  }, [userLoading, userInfo, navigate])

  useEffect(() => {
    if (userInfo?.id) {
      fetchTasks(true)
      fetchTeams()
    }
  }, [userInfo])

  // Real-time subscription for all tasks
  useEffect(() => {
    if (!userInfo?.id) return

    const tasksChannel = supabase
      .channel('all-tasks')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks'
        },
        (payload) => {
          // Use immediate refresh for all real-time updates
          fetchTasks(true)
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_assignments'
        },
        (payload) => {
          // Refresh when assignments change
          fetchTasks(true)
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setRealtimeConnected(true)
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          setRealtimeConnected(false)
        }
      })

    return () => {
      supabase.removeChannel(tasksChannel)
      setRealtimeConnected(false)
    }
  }, [userInfo])

  useEffect(() => {
    filterAndSearchTasks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, searchQuery, filterStatus, filterPriority])

  // Fetch team members when team is selected
  useEffect(() => {
    if (selectedTeam && selectedTeam !== 'all') {
      fetchTeamMembers(selectedTeam)
    } else {
      setTeamMembers([])
    }
  }, [selectedTeam])

  // Fetch team members mapping for all teams
  const fetchAllTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('team_members')
        .select(`
          team_id,
          user_id,
          role,
          users!team_members_user_id_fkey(id, full_name, email)
        `)
      
      if (error) {
        console.error('❌ Supabase error:', error)
        throw error
      }
      
      
      // Flatten the structure to handle nested users object
      const flattenedData = data?.map(tm => ({
        team_id: tm.team_id,
        user_id: tm.user_id,
        role: tm.role,
        users: tm.users
      })) || []
      
      return flattenedData
    } catch (error) {
      console.error('❌ Error fetching team members:', error)
      return []
    }
  }

  // Fetch comments and history when detail modal opens
  useEffect(() => {
    if (showDetailModal && selectedTask) {
      fetchComments(selectedTask.id)
      fetchTaskHistory(selectedTask.id)
      fetchTaskDocuments(selectedTask.id)
      
      // Fetch all team members for grouping in Documents tab
      fetchAllTeamMembers().then(members => {
        setTeamMembers(members)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailModal, selectedTask])

  // Real-time subscription for comments
  useEffect(() => {
    if (!showDetailModal || !selectedTask) return

    const commentsChannel = supabase
      .channel(`task-comments-${selectedTask.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'task_comments',
          filter: `task_id=eq.${selectedTask.id}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            // Add new comment
            fetchComments(selectedTask.id)
          } else if (payload.eventType === 'DELETE') {
            // Remove deleted comment
            setComments(prev => prev.filter(c => c.id !== payload.old.id))
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(commentsChannel)
    }
  }, [showDetailModal, selectedTask])

  // Real-time subscription for task history
  useEffect(() => {
    if (!showDetailModal || !selectedTask) return

    const historyChannel = supabase
      .channel(`task-history-${selectedTask.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'task_history',
          filter: `task_id=eq.${selectedTask.id}`
        },
        () => {
          // Refresh history when new record added
          fetchTaskHistory(selectedTask.id)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(historyChannel)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDetailModal, selectedTask])

  // Real-time subscription for tasks (to update current task data)
  useEffect(() => {
    if (!showDetailModal || !selectedTask) return

    const taskChannel = supabase
      .channel(`task-${selectedTask.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tasks',
          filter: `id=eq.${selectedTask.id}`
        },
        (payload) => {
          // Update selected task with new data
          setSelectedTask(prev => ({
            ...prev,
            ...payload.new
          }))
          // Also update in tasks list
          setTasks(prev => prev.map(t => 
            t.id === payload.new.id ? { ...t, ...payload.new } : t
          ))
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(taskChannel)
    }
  }, [showDetailModal, selectedTask])

  const fetchTasks = async (immediate = false) => {
    // For immediate refresh (after create/update), skip debounce
    if (immediate) {
      try {
        setLoading(true)
        const { data: tasksData, error: tasksError } = await supabase
          .from('tasks')
          .select(`
            *,
            creator:users!tasks_created_by_fkey(full_name)
          `)
          .order('created_at', { ascending: false })

        if (tasksError) {
          console.error('❌ Error fetching tasks:', tasksError)
          throw tasksError
        }


        // Fetch assignments for each task
        const { data: assignmentsData, error: assignError } = await supabase
          .from('task_assignments')
          .select(`
            task_id,
            user_id,
            team_id,
            users!task_assignments_user_id_fkey(id, full_name, email),
            teams(id, name, color)
          `)

        if (assignError) {
          console.error('❌ Error fetching assignments:', assignError)
          throw assignError
        }


        // Fetch all team members to know which users belong to which teams
        const { data: teamMembersData, error: teamMembersError } = await supabase
          .from('team_members')
          .select('team_id, user_id')

        if (teamMembersError) {
          console.error('❌ Error fetching team members:', teamMembersError)
        }


        // Merge assignments with tasks
        const tasksWithAssignments = (tasksData || []).map(task => {
          const taskAssignments = (assignmentsData || []).filter(a => a.task_id === task.id)
          
          return {
            ...task,
            assigned_users: taskAssignments
              .filter(a => a.user_id)
              .map(a => a.users?.id),
            assigned_user_details: taskAssignments
              .filter(a => a.user_id)
              .map(a => a.users),
            assigned_teams: taskAssignments
              .filter(a => a.team_id)
              .map(a => a.teams?.id),
            assigned_team_details: taskAssignments
              .filter(a => a.team_id)
              .map(a => a.teams),
            team_members_map: teamMembersData || [] // Add team membership data
          }
        })

        setTasks(tasksWithAssignments || [])
        setLoading(false)
      } catch (error) {
        console.error('💥 Fatal error in fetchTasks:', error)
        setLoading(false)
      }
      return
    }

    // Debounce for real-time updates
    if (fetchTasksTimeoutRef.current) {
      clearTimeout(fetchTasksTimeoutRef.current)
    }
    
    fetchTasksTimeoutRef.current = setTimeout(async () => {
      try {
        // Silent mode - no loading indicator for background refresh
        const { data: tasksData, error: tasksError } = await supabase
          .from('tasks')
          .select(`
            *,
            creator:users!tasks_created_by_fkey(full_name)
          `)
          .order('created_at', { ascending: false })

        if (tasksError) throw tasksError

        // Fetch assignments for each task
        const { data: assignmentsData, error: assignError } = await supabase
          .from('task_assignments')
          .select(`
            task_id,
            user_id,
            team_id,
            users!task_assignments_user_id_fkey(id, full_name, email),
            teams(id, name, color)
          `)

        if (assignError) throw assignError

        // Fetch all team members
        const { data: teamMembersData } = await supabase
          .from('team_members')
          .select('team_id, user_id')

        // Merge assignments with tasks
        const tasksWithAssignments = (tasksData || []).map(task => {
          const taskAssignments = (assignmentsData || []).filter(a => a.task_id === task.id)
          
          return {
            ...task,
            assigned_users: taskAssignments
              .filter(a => a.user_id)
              .map(a => a.users?.id),
            assigned_user_details: taskAssignments
              .filter(a => a.user_id)
              .map(a => a.users),
            assigned_teams: taskAssignments
              .filter(a => a.team_id)
              .map(a => a.teams?.id),
            assigned_team_details: taskAssignments
              .filter(a => a.team_id)
              .map(a => a.teams),
            team_members_map: teamMembersData || []
          }
        })

        setTasks(tasksWithAssignments || [])
      } catch (error) {
        // Silent error - don't show to user for background refresh
        console.error('Background refresh error:', error)
      }
    }, 100)
  }

  const fetchComments = async (taskId) => {
    try {
      setLoadingComments(true)
      const { data, error } = await supabase
        .from('task_comments')
        .select(`
          *,
          user:users(full_name, email)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setComments(data || [])
    } catch (error) {
      console.error('Error fetching comments:', error)
    } finally {
      setLoadingComments(false)
    }
  }

  // Helper function to format history values (convert UUIDs to names for assignments)
  const formatHistoryValue = async (value, fieldName) => {
    if (!value) return 'empty'
    
    // Check if this is an assignment field
    if (fieldName && (fieldName.includes('assigned') || fieldName.includes('assign'))) {
      try {
        // Try to fetch user name from UUID
        const { data, error } = await supabase
          .from('users')
          .select('full_name, email')
          .eq('id', value)
          .single()
        
        if (data && !error) {
          return data.full_name || data.email
        }
      } catch (err) {
      }
    }
    
    return value
  }

  const fetchTaskHistory = async (taskId) => {
    try {
      const { data, error } = await supabase
        .from('task_history')
        .select(`
          *,
          user:users(full_name, email)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false })

      if (error) throw error
      
      
      // Process history items to convert UUIDs to names
      const processedHistory = await Promise.all((data || []).map(async (item) => {
        if (item.field_name && (item.field_name.includes('assigned') || item.field_name.includes('assign'))) {
          const oldValueName = await formatHistoryValue(item.old_value, item.field_name)
          const newValueName = await formatHistoryValue(item.new_value, item.field_name)
          
          
          return {
            ...item,
            old_value_display: oldValueName,
            new_value_display: newValueName
          }
        }
        return {
          ...item,
          old_value_display: item.old_value || 'empty',
          new_value_display: item.new_value
        }
      }))
      
      setTaskHistory(processedHistory)
    } catch (error) {
      console.error('❌ Error fetching history:', error)
    }
  }

  const fetchTaskDocuments = async (taskId) => {
    try {
      const { data, error } = await supabase
        .from('task_documents')
        .select(`
          *,
          uploaded_by_user:users!task_documents_uploaded_by_fkey(full_name, email)
        `)
        .eq('task_id', taskId)
        .order('created_at', { ascending: false })

      if (error) throw error
      setTaskDocuments(data || [])
    } catch (error) {
      console.error('❌ Error fetching documents:', error)
      setTaskDocuments([])
    }
  }

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !selectedTask) return

    try {
      setUploadingDocument(true)

      // Upload file to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${selectedTask.id}/${Date.now()}.${fileExt}`
      const { error: uploadError } = await supabase.storage
        .from('task-documents')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('task-documents')
        .getPublicUrl(fileName)

      // Save document metadata to database
      const { error: dbError } = await supabase
        .from('task_documents')
        .insert({
          task_id: selectedTask.id,
          file_name: file.name,
          file_path: fileName,
          file_url: publicUrl,
          file_size: file.size,
          file_type: file.type,
          uploaded_by: userInfo.id
        })

      if (dbError) throw dbError

      fetchTaskDocuments(selectedTask.id)
      
      // Reset file input
      e.target.value = ''
    } catch (error) {
      console.error('❌ Error uploading document:', error)
      alert('Failed to upload document: ' + error.message)
    } finally {
      setUploadingDocument(false)
    }
  }

  const handleDeleteDocument = async (docId, filePath) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('task-documents')
        .remove([filePath])

      if (storageError) throw storageError

      // Delete from database
      const { error: dbError } = await supabase
        .from('task_documents')
        .delete()
        .eq('id', docId)

      if (dbError) throw dbError

      fetchTaskDocuments(selectedTask.id)
    } catch (error) {
      console.error('❌ Error deleting document:', error)
      alert('Failed to delete document: ' + error.message)
    }
  }

  const fetchTeams = async () => {
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .order('name')

      if (error) throw error
      setTeams(data || [])
    } catch (error) {
      console.error('Error fetching teams:', error)
    }
  }

  const fetchTeamMembers = async (teamId) => {
    try {
      
      const { data, error } = await supabase
        .from('team_members')
        .select(`
          id,
          user_id,
          role,
          users!team_members_user_id_fkey(
            id,
            full_name,
            email
          )
        `)
        .eq('team_id', teamId)
        .order('role', { ascending: false })

      if (error) {
        console.error('❌ Error from Supabase:', error)
        throw error
      }
      
      
      // Check if no members found
      if (!data || data.length === 0) {
        console.warn('⚠️⚠️⚠️ WARNING: Team has NO members in team_members table!')
        console.warn('🛠️ Please add members to this team via "Manage" button')
        setTeamMembers([])
        return []
      }
      
      // Flatten the structure with null checks
      const members = data?.map(member => {
        if (!member.users) {
          console.warn('⚠️ Member without user data:', member)
          return null
        }
        return {
          member_id: member.id,
          user_id: member.user_id,
          id: member.users.id,
          full_name: member.users.full_name,
          email: member.users.email,
          role: member.role
        }
      }).filter(Boolean) || []
      
      setTeamMembers(members)
      return members
    } catch (error) {
      console.error('❌ Error fetching team members:', error.message)
      
      // Enhanced error messages
      if (error.message.includes('Failed to fetch')) {
        console.error('🌐 Network error: Cannot connect to Supabase')
        console.error('💡 Check your internet connection or Supabase service status')
      } else if (error.code === 'PGRST116') {
        console.error('🔒 RLS Policy error: Check team_members table permissions')
      }
      
      setTeamMembers([])
      return []
    }
  }

  // Fetch members for multiple teams (multi-team selection)
  // eslint-disable-next-line no-unused-vars
  const fetchMultipleTeamMembers = async (teamIds) => {
    try {
      
      const membersMap = {}
      
      for (const teamId of teamIds) {
        const members = await fetchTeamMembers(teamId)
        membersMap[teamId] = members
      }
      
      setAllTeamMembers(membersMap)
    } catch (error) {
      console.error('❌ Error fetching multiple team members:', error)
    }
  }

  // Add team to multi-selection
  const handleAddTeamToSelection = async (teamId) => {
    if (!teamId || teamId === 'all') return
    
    // Check if team already selected
    if (selectedTeams.includes(teamId)) {
      return
    }
    
    
    // Add team to selected teams
    const newSelectedTeams = [...selectedTeams, teamId]
    setSelectedTeams(newSelectedTeams)
    
    // Fetch members for this team
    const members = await fetchTeamMembers(teamId)
    setAllTeamMembers(prev => ({
      ...prev,
      [teamId]: members
    }))
    
    // Initialize empty selection for this team
    setSelectedUsersByTeam(prev => ({
      ...prev,
      [teamId]: []
    }))
    
  }

  // Remove team from multi-selection
  const handleRemoveTeamFromSelection = (teamId) => {
    
    setSelectedTeams(prev => prev.filter(id => id !== teamId))
    
    // Remove team's members
    setAllTeamMembers(prev => {
      const newMap = { ...prev }
      delete newMap[teamId]
      return newMap
    })
    
    // Remove team's user selections
    setSelectedUsersByTeam(prev => {
      const newMap = { ...prev }
      delete newMap[teamId]
      return newMap
    })
  }

  // Toggle user selection within a team
  const handleToggleUserInTeam = (teamId, userId) => {
    setSelectedUsersByTeam(prev => {
      const teamUsers = prev[teamId] || []
      const isSelected = teamUsers.includes(userId)
      
      return {
        ...prev,
        [teamId]: isSelected
          ? teamUsers.filter(id => id !== userId)
          : [...teamUsers, userId]
      }
    })
  }

  // Get all selected user IDs across all teams
  const getAllSelectedUserIds = () => {
    const allUserIds = []
    Object.values(selectedUsersByTeam).forEach(userIds => {
      allUserIds.push(...userIds)
    })
    return [...new Set(allUserIds)] // Remove duplicates
  }

  const handleCreateTeam = async (e) => {
    e.preventDefault()
    
    try {
      const { data, error } = await supabase
        .from('teams')
        .insert([
          {
            name: teamFormData.name,
            description: teamFormData.description,
            color: teamFormData.color,
            created_by: userInfo.id
          }
        ])
        .select()
        .single()

      if (error) throw error
      
      setTeams([...teams, data])
      setShowTeamModal(false)
      setTeamFormData({ name: '', description: '', color: '#3B82F6' })
      alert('Team berhasil dibuat!')
    } catch (error) {
      console.error('Error creating team:', error)
      alert('Gagal membuat team: ' + error.message)
    }
  }

  const fetchAvailableUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('full_name')

      if (error) throw error
      setAvailableUsers(data || [])
    } catch (error) {
      console.error('Error fetching users:', error.message)
      alert('Error fetching users: ' + error.message)
    }
  }

  const handleOpenMembersModal = async (team) => {
    setManagingTeam(team)
    setShowMembersModal(true)
    try {
      await Promise.all([
        fetchTeamMembers(team.id),
        fetchAvailableUsers()
      ])
    } catch (error) {
      console.error('Error loading team data:', error)
    }
  }

  const handleAddMember = async (userId, role = 'member') => {
    if (!managingTeam) return

    try {
      const { error } = await supabase
        .from('team_members')
        .insert([
          {
            team_id: managingTeam.id,
            user_id: userId,
            role: role
          }
        ])

      if (error) throw error
      
      // Refresh team members
      await fetchTeamMembers(managingTeam.id)
      alert('Member berhasil ditambahkan!')
    } catch (error) {
      console.error('Error adding member:', error)
      if (error.code === '23505') {
        alert('User sudah menjadi anggota tim ini!')
      } else {
        alert('Gagal menambahkan member: ' + error.message)
      }
    }
  }

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Hapus member dari tim ini?')) return

    try {
      const { error } = await supabase
        .from('team_members')
        .delete()
        .eq('team_id', managingTeam.id)
        .eq('user_id', memberId)

      if (error) throw error
      
      // Refresh team members
      await fetchTeamMembers(managingTeam.id)
      alert('Member berhasil dihapus!')
    } catch (error) {
      console.error('Error removing member:', error)
      alert('Gagal menghapus member: ' + error.message)
    }
  }

  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'leader' ? 'member' : 'leader'
    
    try {
      const { error } = await supabase
        .from('team_members')
        .update({ role: newRole })
        .eq('team_id', managingTeam.id)
        .eq('user_id', userId)

      if (error) throw error
      
      // Refresh team members
      await fetchTeamMembers(managingTeam.id)
    } catch (error) {
      console.error('Error updating role:', error)
      alert('Gagal mengubah role: ' + error.message)
    }
  }

  const filterAndSearchTasks = () => {
    let filtered = [...tasks]

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(task => task.status === filterStatus)
    }

    // Priority filter
    if (filterPriority !== 'all') {
      filtered = filtered.filter(task => task.priority === filterPriority)
    }

    setFilteredTasks(filtered)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const taskData = {
        title: formData.title,
        description: formData.description || null,
        project_code: formData.project_code || null,
        status: formData.status,
        priority: formData.priority,
        due_date: formData.due_date || null,
        created_by: userInfo.id,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
      }


      let taskId

      if (editMode && selectedTask) {
        // Update existing task
        
        // Add updated_by for task_history trigger
        const updateData = {
          ...taskData,
          updated_by: userInfo.id
        }
        
        const { error } = await supabase
          .from('tasks')
          .update(updateData)
          .eq('id', selectedTask.id)

        if (error) throw error
        taskId = selectedTask.id

        // Delete existing assignments
        await supabase
          .from('task_assignments')
          .delete()
          .eq('task_id', taskId)

      } else {
        // Create new task
        const { data, error } = await supabase
          .from('tasks')
          .insert([taskData])
          .select()
          .single()

        if (error) {
          console.error('❌ Error creating task:', error)
          throw error
        }
        taskId = data.id
      }

      // Determine which users to assign based on multi-team or legacy mode
      let usersToAssign = []
      let teamsToAssign = []

      if (selectedTeams.length > 0) {
        // Multi-team mode: collect all selected users from all teams
        usersToAssign = getAllSelectedUserIds()
        teamsToAssign = selectedTeams
      } else {
        // Legacy mode: use formData.assigned_users and selectedTeam
        usersToAssign = formData.assigned_users
        if (selectedTeam && selectedTeam !== 'all') {
          teamsToAssign = [selectedTeam]
        }
      }

      // Create new assignments for users
      if (usersToAssign.length > 0) {
        const userAssignments = usersToAssign.map(userId => ({
          task_id: taskId,
          user_id: userId,
          assigned_by: userInfo.id
        }))

        const { error: assignError } = await supabase
          .from('task_assignments')
          .insert(userAssignments)

        if (assignError) {
          console.error('❌ Error assigning users:', assignError)
          throw assignError
        }
      }

      // Create team assignments for all selected teams
      if (teamsToAssign.length > 0) {
        const teamAssignments = teamsToAssign.map(teamId => ({
          task_id: taskId,
          team_id: teamId,
          assigned_by: userInfo.id
        }))

        const { error: teamAssignError } = await supabase
          .from('task_assignments')
          .insert(teamAssignments)

        if (teamAssignError) {
          console.error('❌ Error assigning teams:', teamAssignError)
          throw teamAssignError
        }
      }

      setShowModal(false)
      resetForm()
      // Immediate refresh to show task instantly
      await fetchTasks(true)
    } catch (error) {
      console.error('Error saving task:', error)
      alert('Gagal menyimpan task: ' + error.message)
    }
  }

  const handleDelete = (taskId) => {
    setTaskToDelete(taskId)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!taskToDelete) return

    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', taskToDelete)

      if (error) throw error
      await fetchTasks(true)
      setShowDetailModal(false)
      setShowDeleteConfirm(false)
      setTaskToDelete(null)
    } catch (error) {
      console.error('Error deleting task:', error)
      alert('Gagal menghapus task: ' + error.message)
    }
  }

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ status: newStatus })
        .eq('id', taskId)

      if (error) throw error
      await fetchTasks(true)
    } catch (error) {
      console.error('Error updating status:', error)
    }
  }

  const openEditModal = async (task) => {
    setSelectedTask(task)
    setFormData({
      title: task.title,
      description: task.description || '',
      project_code: task.project_code || '',
      status: task.status,
      priority: task.priority,
      due_date: task.due_date || '',
      assigned_users: task.assigned_users || [],
      tags: task.tags?.join(', ') || ''
    })
    
    // Load multi-team selection if task has assigned teams
    if (task.assigned_teams && task.assigned_teams.length > 0) {
      setSelectedTeams(task.assigned_teams)
      
      // Fetch members for all assigned teams
      const membersMap = {}
      const selectedUsersMap = {}
      
      for (const teamId of task.assigned_teams) {
        const members = await fetchTeamMembers(teamId)
        // Filter out null/undefined members
        const validMembers = members.filter(m => m && m.user_id && m.full_name)
        membersMap[teamId] = validMembers
        
        // Set which users are selected for this team
        const teamUserIds = (task.assigned_users || []).filter(userId => 
          validMembers.some(m => m.user_id === userId)
        )
        selectedUsersMap[teamId] = teamUserIds
      }
      
      setAllTeamMembers(membersMap)
      setSelectedUsersByTeam(selectedUsersMap)
    }
    
    setEditMode(true)
    setShowModal(true)
  }

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0] // Get today's date in YYYY-MM-DD format
    setFormData({
      title: '',
      description: '',
      project_code: '',
      status: 'todo',
      priority: 'medium',
      due_date: today, // Set default to today
      assigned_users: [],
      tags: ''
    })
    setEditMode(false)
    setSelectedTask(null)
    
    // Reset multi-team selection state
    setSelectedTeams([])
    setAllTeamMembers({})
    setSelectedUsersByTeam({})
    setSelectedTeam('all')
    setTeamMembers([])
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTask) return

    try {
      const { data, error } = await supabase
        .from('task_comments')
        .insert([
          {
            task_id: selectedTask.id,
            user_id: userInfo.id,
            comment: newComment.trim()
          }
        ])
        .select(`
          *,
          user:users(full_name, email)
        `)
        .single()

      if (error) throw error
      setComments([data, ...comments])
      setNewComment('')
    } catch (error) {
      console.error('Error adding comment:', error)
      alert('Gagal menambahkan komentar')
    }
  }

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Hapus komentar ini?')) return

    try {
      const { error } = await supabase
        .from('task_comments')
        .delete()
        .eq('id', commentId)

      if (error) throw error
      setComments(comments.filter((c) => c.id !== commentId))
    } catch (error) {
      console.error('Error deleting comment:', error)
      alert('Gagal menghapus komentar')
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'todo': return <Circle className="w-4 h-4" />
      case 'in_progress': return <Clock className="w-4 h-4" />
      case 'review': return <AlertCircle className="w-4 h-4" />
      case 'done': return <CheckCircle2 className="w-4 h-4" />
      default: return <Circle className="w-4 h-4" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-gray-100 text-gray-700 border-gray-300'
      case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'review': return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'done': return 'bg-green-100 text-green-700 border-green-300'
      default: return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-gray-100 text-gray-600'
      case 'medium': return 'bg-blue-100 text-blue-600'
      case 'high': return 'bg-orange-100 text-orange-600'
      case 'urgent': return 'bg-red-100 text-red-600'
      default: return 'bg-gray-100 text-gray-600'
    }
  }

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'todo': return 'bg-gray-50'
      case 'in_progress': return 'bg-blue-50'
      case 'review': return 'bg-yellow-50'
      case 'done': return 'bg-green-50'
      default: return 'bg-gray-50'
    }
  }

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case 'low': return 'bg-gray-50'
      case 'medium': return 'bg-blue-50'
      case 'high': return 'bg-orange-50'
      case 'urgent': return 'bg-red-50'
      default: return 'bg-gray-50'
    }
  }

  const statusColumns = {
    todo: { title: 'To Do', tasks: filteredTasks.filter(t => t.status === 'todo') },
    in_progress: { title: 'In Progress', tasks: filteredTasks.filter(t => t.status === 'in_progress') },
    review: { title: 'Review', tasks: filteredTasks.filter(t => t.status === 'review') },
    done: { title: 'Done', tasks: filteredTasks.filter(t => t.status === 'done') }
  }

  return (
    <div className="relative flex flex-col min-h-[calc(100vh-4rem)] -m-4 sm:-m-6 lg:-m-8">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-100/30 via-blue-100/30 to-blue-100/30 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-gradient-to-tl from-blue-100/30 via-pink-100/30 to-blue-100/30 rounded-full blur-3xl animate-float-delayed"></div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1 py-20">
          <div className="text-center">
            <div className="relative inline-block">
              {/* Spinner */}
              <div className="w-16 h-16 relative">
                <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-500 border-r-blue-500 animate-spin"></div>
                <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-b-blue-500 border-l-pink-500 animate-spin-reverse"></div>
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 animate-pulse shadow-lg"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full shadow-md animate-ping"></div>
                </div>
              </div>
            </div>
            <p className="mt-6 text-gray-700 font-medium text-base animate-pulse">Loading tasks...</p>
          </div>
        </div>
      ) : (
        <>
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-16 z-20 shadow-sm">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-600 bg-clip-text text-transparent">
                  Task Management
                </h1>
                {/* Real-time indicator */}
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-200 rounded-full shadow-sm">
                  {realtimeConnected ? (
                    <>
                      <Wifi className="w-3.5 h-3.5 text-green-600" />
                      <span className="text-xs font-medium text-green-600">Live</span>
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-500">Offline</span>
                    </>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {filteredTasks.length} tasks • {statusColumns.done.tasks.length} completed
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('board')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'board' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                  }`}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                  }`}
                >
                  <Menu className="w-4 h-4" />
                </button>
              </div>

              {/* Add Task Button */}
              <button
                onClick={() => {
                  resetForm()
                  setShowModal(true)
                }}
                className="flex items-center justify-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-xl hover:from-blue-700 hover:to-blue-700 active:scale-95 transition-all shadow-lg hover:shadow-xl font-bold text-base min-h-[48px] w-full sm:w-auto"
              >
                <Plus className="w-5 h-5" />
                <span>New Task</span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium min-h-[48px]"
              />
            </div>

            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center gap-2 px-5 py-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all font-semibold min-h-[48px] w-full sm:w-auto"
            >
              <Filter className="w-5 h-5" />
              <span>Filters</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Filter Options */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-3 overflow-hidden"
              >
                <div className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded-lg">
                  {/* Team Filter */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Team
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedTeam}
                        onChange={(e) => setSelectedTeam(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">All Teams</option>
                        {teams.map((team) => (
                          <option key={team.id} value={team.id}>
                            {team.name}
                          </option>
                        ))}
                      </select>
                      {selectedTeam !== 'all' && (
                        <button
                          onClick={() => {
                            const team = teams.find(t => t.id === selectedTeam)
                            if (team) handleOpenMembersModal(team)
                          }}
                          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium whitespace-nowrap"
                        >
                          Manage
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Status</label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Status</option>
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  {/* Priority Filter */}
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Priority</label>
                    <select
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Priority</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Reset Filters */}
                  {(filterStatus !== 'all' || filterPriority !== 'all' || selectedTeam !== 'all') && (
                    <button
                      onClick={() => {
                        setFilterStatus('all')
                        setFilterPriority('all')
                        setSelectedTeam('all')
                      }}
                      className="self-end px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Reset Filters
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Main Content */}
      <main className="relative z-10 flex-1">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {viewMode === 'board' ? (
          // Board View (Kanban)
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {Object.entries(statusColumns).map(([status, column]) => (
              <div key={status} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    {getStatusIcon(status)}
                    {column.title}
                  </h3>
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    {column.tasks.length}
                  </span>
                </div>

                <div className="space-y-3">
                  <AnimatePresence>
                    {column.tasks.map((task) => (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer group"
                        onClick={() => {
                          setSelectedTask(task)
                          setShowDetailModal(true)
                        }}
                      >
                        {/* Priority Badinge */}
                        <div className="flex items-start justify-between mb-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              openEditModal(task)
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                          </button>
                        </div>

                        {/* Title */}
                        <h4 className="font-medium text-gray-900 mb-2 line-clamp-2">
                          {task.title}
                        </h4>

                        {/* Description */}
                        {task.description && (
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {task.description}
                          </p>
                        )}

                        {/* Tags */}
                        {task.tags && task.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {task.tags.slice(0, 2).map((tag, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                {tag}
                              </span>
                            ))}
                            {task.tags.length > 2 && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                +{task.tags.length - 2}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center gap-1 flex-wrap">
                            <User className="w-3 h-3" />
                            {task.assigned_user_details?.length > 0 ? (
                              <div className="flex items-center gap-1 flex-wrap">
                                {task.assigned_user_details.slice(0, 2).map((user, idx) => (
                                  <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                                    {user?.full_name}
                                  </span>
                                ))}
                                {task.assigned_user_details.length > 2 && (
                                  <span className="text-gray-500">+{task.assigned_user_details.length - 2}</span>
                                )}
                              </div>
                            ) : task.assigned_team_details?.length > 0 ? (
                              <div className="flex items-center gap-1 flex-wrap">
                                {task.assigned_team_details.slice(0, 2).map((team, idx) => (
                                  <span 
                                    key={idx} 
                                    className="px-2 py-0.5 rounded-full text-xs text-white"
                                    style={{ backgroundColor: team?.color || '#3B82F6' }}
                                  >
                                    {team?.name}
                                  </span>
                                ))}
                                {task.assigned_team_details.length > 2 && (
                                  <span className="text-gray-500">+{task.assigned_team_details.length - 2}</span>
                                )}
                              </div>
                            ) : (
                              <span>Unassigned</span>
                            )}
                          </div>
                          {task.due_date && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(task.due_date).toLocaleDateString('id-ID')}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // List View
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{task.title}</div>
                          {task.description && (
                            <div className="text-sm text-gray-500 line-clamp-1">{task.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(task.status)}`}
                        >
                          <option value="todo">To Do</option>
                          <option value="in_progress">In Progress</option>
                          <option value="review">Review</option>
                          <option value="done">Done</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex flex-col gap-1">
                          {task.assigned_user_details?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {task.assigned_user_details.map((user, idx) => (
                                <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs">
                                  {user?.full_name}
                                </span>
                              ))}
                            </div>
                          )}
                          {task.assigned_team_details?.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {task.assigned_team_details.map((team, idx) => (
                                <span 
                                  key={idx} 
                                  className="px-2 py-0.5 rounded-full text-xs text-white"
                                  style={{ backgroundColor: team?.color || '#3B82F6' }}
                                >
                                  {team?.name}
                                </span>
                              ))}
                            </div>
                          )}
                          {!task.assigned_user_details?.length && !task.assigned_team_details?.length && (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {task.due_date ? new Date(task.due_date).toLocaleDateString('id-ID') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedTask(task)
                              setShowDetailModal(true)
                            }}
                            className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5 text-gray-600" />
                          </button>
                          <button
                            onClick={() => openEditModal(task)}
                            className="p-2.5 hover:bg-blue-50 active:bg-blue-100 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                            title="Edit Task"
                          >
                            <Edit className="w-5 h-5 text-blue-600" />
                          </button>
                          <button
                            onClick={() => handleDelete(task.id)}
                            className="p-2.5 hover:bg-red-50 active:bg-red-100 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                            title="Delete Task"
                          >
                            <Trash2 className="w-5 h-5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      </main>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            {/* Backdrop with blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-xl w-full h-[80vh] sm:h-auto sm:max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
              <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  {editMode ? 'Edit Task' : 'Create New Task'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-2 sm:space-y-3">
                {/* Title */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Task Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-1.5 sm:px-4 sm:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter task title"
                  />
                </div>

                {/* Kode Project */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    Kode Project
                  </label>
                  <input
                    type="text"
                    value={formData.project_code}
                    onChange={(e) => setFormData({ ...formData, project_code: e.target.value })}
                    className="w-full px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs sm:text-sm"
                    placeholder="e.g., PRJ-2025-001"
                  />
                  <p className="mt-0.5 text-[10px] sm:text-xs text-gray-500">Optional: Enter project code or identifier</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-1.5 sm:px-4 sm:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter task description"
                  />
                </div>

                {/* Status and Priority */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className={`w-full px-3 py-1.5 sm:px-4 sm:py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${getStatusBgColor(formData.status)} ${getStatusColor(formData.status)}`}
                    >
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="review">Review</option>
                      <option value="done">Done</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                      className={`w-full px-3 py-1.5 sm:px-4 sm:py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${getPriorityBgColor(formData.priority)} ${getPriorityColor(formData.priority)}`}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                {/* Due Date and Team/Assignment */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Due Date
                    </label>
                    <div className="relative">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        className="w-full pl-3 sm:pl-10 pr-3 py-2 text-sm border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[42px] bg-white"
                      />
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none sm:hidden" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Select Team to Add
                    </label>
                    <div className="flex gap-1.5 sm:gap-2">
                      <select
                        value={selectedTeam}
                        onChange={(e) => {
                          const newTeam = e.target.value
                          setSelectedTeam(newTeam)
                          setFormData({ ...formData, assigned_to: '' })
                          // Fetch team members when team changes
                          if (newTeam && newTeam !== 'all') {
                            fetchTeamMembers(newTeam)
                          } else {
                            setTeamMembers([])
                          }
                        }}
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      >
                        <option value="all">Select a team to add</option>
                        {teams
                          .filter(team => !selectedTeams.includes(team.id))
                          .map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedTeam && selectedTeam !== 'all') {
                            handleAddTeamToSelection(selectedTeam)
                            setSelectedTeam('all')
                          }
                        }}
                        disabled={!selectedTeam || selectedTeam === 'all'}
                        className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:from-emerald-600 hover:to-green-700 transition-all duration-200 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed flex items-center gap-1.5 font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:-translate-y-0.5 disabled:shadow-none disabled:transform-none whitespace-nowrap text-sm"
                        title="Add team to selection"
                      >
                        <Plus className="w-4 h-4" />
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowTeamModal(true)}
                        className="p-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg hover:from-blue-700 hover:to-blue-700 transition-all duration-200 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:-translate-y-0.5 flex items-center justify-center flex-shrink-0"
                        title="Create new team"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Selected Teams Display */}
                {selectedTeams.length > 0 && (
                  <div className="border-2 border-blue-200 rounded-lg p-2 sm:p-3 bg-blue-50">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs sm:text-sm font-semibold text-blue-900 flex items-center gap-1.5 sm:gap-2">
                        <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                        Selected Teams ({selectedTeams.length})
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedTeams([])
                          setAllTeamMembers({})
                          setSelectedUsersByTeam({})
                          setFormData({ ...formData, assigned_users: [] })
                        }}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      {selectedTeams.map(teamId => {
                        const team = teams.find(t => t.id === teamId)
                        const members = allTeamMembers[teamId] || []
                        const selectedUsers = selectedUsersByTeam[teamId] || []
                        
                        return (
                          <div key={teamId} className="bg-white rounded-lg border border-blue-200 p-2 sm:p-3">
                            <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                              <h5 className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center gap-1.5 sm:gap-2">
                                <div 
                                  className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full"
                                  style={{ backgroundColor: team?.color || '#3B82F6' }}
                                />
                                {team?.name}
                              </h5>
                              <button
                                type="button"
                                onClick={() => handleRemoveTeamFromSelection(teamId)}
                                className="text-[10px] sm:text-xs text-red-600 hover:text-red-700 font-medium"
                              >
                                Remove Team
                              </button>
                            </div>
                            
                            {members.length === 0 ? (
                              <div className="text-[10px] sm:text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-1.5 sm:p-2">
                                ⚠️ No members in this team. Add members via Manage button.
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {members
                                  .filter(member => member && member.user_id && member.full_name)
                                  .map(member => (
                                  <label
                                    key={member.user_id}
                                    className="flex items-center gap-1.5 sm:gap-2 p-1 sm:p-1.5 hover:bg-blue-50 rounded cursor-pointer transition-colors"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedUsers.includes(member.user_id)}
                                      onChange={() => handleToggleUserInTeam(teamId, member.user_id)}
                                      className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                    />
                                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold">
                                      {member.full_name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-[10px] sm:text-xs font-medium text-gray-900 leading-tight">
                                        {member.full_name} {member.role === 'leader' && '⭐'}
                                      </p>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            )}
                            
                            {selectedUsers.length > 0 && (
                              <div className="mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-blue-700 font-medium">
                                ✓ {selectedUsers.length} member(s) selected from {team?.name}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    
                    {/* Total selected users summary */}
                    <div className="mt-2 pt-2 border-t border-blue-200">
                      <p className="text-xs sm:text-sm font-semibold text-blue-900">
                        Total: {getAllSelectedUserIds().length} user(s) selected across {selectedTeams.length} team(s)
                      </p>
                    </div>
                  </div>
                )}

                {/* Legacy single team assignment (shown only if no multi-team selection) */}
                {selectedTeams.length === 0 && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      Or Assign To Team Members (Legacy - Single Team)
                    </label>
                    {selectedTeam === 'all' || !selectedTeam ? (
                      <div className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 text-xs sm:text-sm">
                        👆 Select a team above and click "Add" to use multi-team selection
                      </div>
                    ) : teamMembers.length === 0 ? (
                      <div className="w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-yellow-300 rounded-lg bg-yellow-50 text-yellow-800">
                        <div className="flex items-start gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                          <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-xs sm:text-sm font-semibold">⚠️ Team has no members!</p>
                            <p className="text-[10px] sm:text-xs mt-0.5 sm:mt-1">You need to add members to "{teams.find(t => t.id === selectedTeam)?.name}" team first</p>
                          </div>
                        </div>
                        <div className="ml-5 sm:ml-7 mt-1.5 sm:mt-2 p-1.5 sm:p-2 bg-white rounded border border-yellow-200">
                          <p className="text-[10px] sm:text-xs font-medium mb-0.5 sm:mb-1">How to add members:</p>
                          <ol className="text-[10px] sm:text-xs space-y-0.5 list-decimal list-inside">
                            <li>Close this modal</li>
                            <li>Click <span className="font-semibold bg-blue-100 px-1 rounded">"Manage"</span> button next to team dropdown in filters</li>
                            <li>Add users (bayu, kora, contoh, etc) to the team</li>
                            <li>Come back and create the task</li>
                          </ol>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-1.5 sm:space-y-2 max-h-40 sm:max-h-48 overflow-y-auto border border-gray-300 rounded-lg p-2 sm:p-3 bg-white">
                          {teamMembers
                            .filter(member => member && member.user_id && member.full_name)
                            .map((member) => (
                            <label
                              key={member.user_id}
                              className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors group"
                            >
                              <input
                                type="checkbox"
                                checked={formData.assigned_users.includes(member.user_id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData({
                                      ...formData,
                                      assigned_users: [...formData.assigned_users, member.user_id]
                                    })
                                  } else {
                                    setFormData({
                                      ...formData,
                                      assigned_users: formData.assigned_users.filter(id => id !== member.user_id)
                                    })
                                  }
                                }}
                                className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              />
                              <div className="flex items-center gap-1.5 sm:gap-2 flex-1">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold group-hover:scale-110 transition-transform">
                                  {member.full_name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                    {member.full_name} {member.role === 'leader' && '⭐'}
                                  </p>
                                  <p className="text-[10px] sm:text-xs text-gray-500 truncate">{member.email}</p>
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                        {formData.assigned_users.length > 0 && (
                          <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2 px-2 py-1.5 sm:px-3 sm:py-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                            <p className="text-xs sm:text-sm text-blue-700 font-medium">
                              {formData.assigned_users.length} member(s) selected from this team
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Tags */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full px-3 py-1.5 sm:px-4 sm:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., frontend, bug, feature"
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t mt-3 sm:mt-4">
                  <button
                    type="submit"
                    className="flex-1 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 via-blue-600 to-blue-600 text-white rounded-lg sm:rounded-xl hover:from-blue-700 hover:via-blue-700 hover:to-blue-700 transition-all duration-200 font-semibold text-sm sm:text-base shadow-lg shadow-blue-500/40 hover:shadow-xl hover:shadow-blue-500/50 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                  >
                    {editMode ? (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Update Task
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Create Task
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false)
                      resetForm()
                    }}
                    className="px-6 py-2 sm:px-8 sm:py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-semibold text-sm sm:text-base hover:-translate-y-0.5"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedTask && (
          <>
            {/* Backdrop with blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
              onClick={() => setShowDetailModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-xl w-full h-[95vh] sm:h-auto sm:max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
              <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Task Details</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setShowDetailModal(false)
                      openEditModal(selectedTask)
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Edit className="w-5 h-5 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(selectedTask.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </button>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Title */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">{selectedTask.title}</h3>
                </div>

                {/* Status and Priority */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor(selectedTask.status)}`}>
                    {getStatusIcon(selectedTask.status)}
                    <span className="text-sm font-medium">
                      {statusColumns[selectedTask.status]?.title}
                    </span>
                  </div>
                  <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getPriorityColor(selectedTask.priority)}`}>
                    {selectedTask.priority.charAt(0).toUpperCase() + selectedTask.priority.slice(1)}
                  </span>
                </div>

                {/* Project Code */}
                {selectedTask.project_code && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Kode Project
                    </h4>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-50 border border-blue-200 rounded-lg">
                      <code className="text-sm font-mono font-semibold text-blue-700">{selectedTask.project_code}</code>
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedTask.description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                    <p className="text-gray-600 whitespace-pre-wrap">{selectedTask.description}</p>
                  </div>
                )}

                {/* Meta Info */}
                <div className="grid grid-cols-1 gap-4">
                  {/* Assigned Teams with Members */}
                  {selectedTask.assigned_team_details?.length > 0 && (
                    <div className="flex items-start gap-2 text-sm">
                      <Users className="w-4 h-4 text-gray-400 mt-1" />
                      <div className="flex-1">
                        <span className="text-gray-700 block mb-2 font-medium">Assigned to teams:</span>
                        <div className="space-y-3">
                          {selectedTask.assigned_team_details.map((team) => {
                            
                            // Filter users who are ACTUALLY members of this team
                            const teamMemberIds = (selectedTask.team_members_map || [])
                              .filter(tm => tm.team_id === team.id)
                              .map(tm => tm.user_id)
                            
                            const teamAssignedUsers = (selectedTask.assigned_user_details || [])
                              .filter(user => teamMemberIds.includes(user.id))
                            

                            return (
                              <div key={team.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                {/* Team Badge */}
                                <div className="flex items-center gap-2 mb-2">
                                  <span 
                                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium text-white"
                                    style={{ backgroundColor: team?.color || '#3B82F6' }}
                                  >
                                    <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-white text-xs font-bold">
                                      {team?.name?.charAt(0)?.toUpperCase()}
                                    </div>
                                    {team?.name}
                                  </span>
                                </div>
                                
                                {/* Team Members */}
                                {teamAssignedUsers.length > 0 && (
                                  <div className="pl-2">
                                    <span className="text-xs text-gray-600 block mb-1.5">Members:</span>
                                    <div className="flex flex-wrap gap-1.5">
                                      {teamAssignedUsers.map((user) => (
                                        <span key={user.id} className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-1 rounded-md text-xs border border-gray-200">
                                          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-[10px] font-semibold">
                                            {user?.full_name?.charAt(0)?.toUpperCase()}
                                          </div>
                                          {user?.full_name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )
                          })}
                          
                          {/* Show users who are NOT in any team */}
                          {(() => {
                            const allTeamMemberIds = (selectedTask.team_members_map || [])
                              .map(tm => tm.user_id)
                            const usersWithoutTeam = (selectedTask.assigned_user_details || [])
                              .filter(user => !allTeamMemberIds.includes(user.id))
                            
                            if (usersWithoutTeam.length > 0) {
                              return (
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-gray-500 text-white">
                                      <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-white text-xs font-bold">
                                        ?
                                      </div>
                                      Individual Assignments
                                    </span>
                                  </div>
                                  <div className="pl-2">
                                    <span className="text-xs text-gray-600 block mb-1.5">Users:</span>
                                    <div className="flex flex-wrap gap-1.5">
                                      {usersWithoutTeam.map((user) => (
                                        <span key={user.id} className="inline-flex items-center gap-1.5 bg-white text-gray-700 px-2 py-1 rounded-md text-xs border border-gray-200">
                                          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white text-[10px] font-semibold">
                                            {user?.full_name?.charAt(0)?.toUpperCase()}
                                          </div>
                                          {user?.full_name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                            return null
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedTask.due_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">Due date:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(selectedTask.due_date).toLocaleDateString('id-ID', { 
                          weekday: 'long', 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">Created by:</span>
                    <span className="font-medium text-gray-900">{selectedTask.creator?.full_name}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">Created:</span>
                    <span className="font-medium text-gray-900">
                      {new Date(selectedTask.created_at).toLocaleDateString('id-ID')}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                {selectedTask.tags && selectedTask.tags.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Tags
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTask.tags.map((tag, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Activity Tabs */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex gap-2 sm:gap-4 mb-4 border-b border-gray-200 overflow-x-auto">
                    <button
                      onClick={() => setActiveTab('comments')}
                      className={`pb-3 px-3 sm:px-4 text-sm font-semibold transition-all relative whitespace-nowrap min-h-[44px] flex items-center ${
                        activeTab === 'comments'
                          ? 'text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Comments
                      {activeTab === 'comments' && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('history')}
                      className={`pb-3 px-3 sm:px-4 text-sm font-semibold transition-all relative whitespace-nowrap min-h-[44px] flex items-center ${
                        activeTab === 'history'
                          ? 'text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      History
                      {activeTab === 'history' && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('workflow')}
                      className={`pb-3 px-3 sm:px-4 text-sm font-semibold transition-all relative whitespace-nowrap min-h-[44px] flex items-center ${
                        activeTab === 'workflow'
                          ? 'text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Workflow
                      {activeTab === 'workflow' && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('activity')}
                      className={`pb-3 px-3 sm:px-4 text-sm font-semibold transition-all relative whitespace-nowrap min-h-[44px] flex items-center ${
                        activeTab === 'activity'
                          ? 'text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Activity
                      {activeTab === 'activity' && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                        />
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('documents')}
                      className={`pb-3 px-3 sm:px-4 text-sm font-semibold transition-all relative whitespace-nowrap min-h-[44px] flex items-center ${
                        activeTab === 'documents'
                          ? 'text-blue-600'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      Documents
                      {activeTab === 'documents' && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                        />
                      )}
                    </button>
                  </div>

                  {/* Tab Content */}
                  <div className="min-h-[300px]">
                    {/* Comments Tab */}
                    {activeTab === 'comments' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-4"
                      >
                        {/* Add Comment */}
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <textarea
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Add a comment..."
                              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-400 resize-none font-medium"
                              rows={3}
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleAddComment}
                          disabled={!newComment.trim() || loadingComments}
                          className="px-5 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-bold shadow-md hover:shadow-lg min-h-[48px]"
                        >
                          {loadingComments ? 'Posting...' : 'Post Comment'}
                        </button>

                        {/* Comments List */}
                        <div className="space-y-4 mt-6">
                          {loadingComments ? (
                            <div className="py-2">
                              <TableSkeleton rows={3} />
                            </div>
                          ) : comments.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              No comments yet. Be the first to comment!
                            </div>
                          ) : (
                            comments.map((comment) => (
                              <div key={comment.id} className="flex gap-3 p-4 bg-gray-50 rounded-lg">
                                <div className="flex-1">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900">
                                        {comment.user.full_name}
                                      </span>
                                      <span className="text-sm text-gray-500">
                                        {new Date(comment.created_at).toLocaleString('id-ID')}
                                      </span>
                                    </div>
                                    {comment.user_id === userInfo.id && (
                                      <button
                                        onClick={() => handleDeleteComment(comment.id)}
                                        className="p-2 hover:bg-red-50 active:bg-red-100 rounded-xl transition-all min-w-[36px] min-h-[36px] flex items-center justify-center"
                                        title="Delete Comment"
                                      >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                      </button>
                                    )}
                                  </div>
                                  <p className="text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}

                    {/* History Tab */}
                    {activeTab === 'history' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-3"
                      >
                        {taskHistory.length === 0 ? (
                          <div className="text-center py-8 text-gray-500">No history available</div>
                        ) : (
                          <div className="space-y-4">
                            {taskHistory.map((item) => (
                              <div key={item.id} className="flex gap-3 items-start">
                                <div className="w-2 h-2 bg-blue-600 rounded-full mt-2" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-gray-900">
                                      {item.user.full_name}
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      {item.change_type.replace('_', ' ')}
                                    </span>
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {item.field_name && (
                                      <span>
                                        Changed <strong>{item.field_name}</strong> from{' '}
                                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                          {item.old_value_display || item.old_value || 'empty'}
                                        </span>{' '}
                                        to{' '}
                                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                          {item.new_value_display || item.new_value}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {new Date(item.created_at).toLocaleString('id-ID')}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* Workflow Tab */}
                    {activeTab === 'workflow' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <div className="flex items-center justify-between py-8">
                          {Object.entries(statusColumns).map(([status, config], idx) => (
                            <React.Fragment key={status}>
                              <div className="flex flex-col items-center gap-2">
                                <div
                                  className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                    selectedTask.status === status
                                      ? 'bg-blue-600 text-white scale-110'
                                      : 'bg-gray-200 text-gray-600'
                                  } transition-all`}
                                >
                                  {getStatusIcon(status)}
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  {config.title}
                                </span>
                              </div>
                              {idx < Object.keys(statusColumns).length - 1 && (
                                <div className="flex-1 h-0.5 bg-gray-300 mx-2" />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                        <div className="text-center text-sm text-gray-600 mt-4">
                          Current status: <strong>{statusColumns[selectedTask.status]?.title}</strong>
                        </div>
                      </motion.div>
                    )}

                    {/* Activity Tab */}
                    {activeTab === 'activity' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-4"
                      >
                        {[...comments, ...taskHistory]
                          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                          .slice(0, 10)
                          .map((item) => (
                            <div key={item.id} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-medium text-gray-900">
                                    {item.user?.full_name}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {item.comment ? 'commented' : item.change_type?.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">
                                  {item.comment || (
                                    item.field_name && (item.field_name.includes('assigned') || item.field_name.includes('assign'))
                                      ? `Changed ${item.field_name}: ${item.old_value_display || item.old_value || 'empty'} → ${item.new_value_display || item.new_value}`
                                      : `Changed ${item.field_name}`
                                  )}
                                </p>
                                <div className="text-xs text-gray-500 mt-1">
                                  {new Date(item.created_at).toLocaleString('id-ID')}
                                </div>
                              </div>
                            </div>
                          ))}
                        {comments.length === 0 && taskHistory.length === 0 && (
                          <div className="text-center py-8 text-gray-500">No activity yet</div>
                        )}
                      </motion.div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="space-y-4"
                      >

                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Task Documents</h4>
                          
                          {/* Upload Document */}
                          <div className="mb-4">
                            <label className="block">
                              <div className="flex items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 transition-colors cursor-pointer bg-gray-50 hover:bg-blue-50">
                                <div className="text-center">
                                  {uploadingDocument ? (
                                    <div className="flex items-center gap-2 text-blue-600">
                                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                      <span className="text-sm font-medium">Uploading...</span>
                                    </div>
                                  ) : (
                                    <>
                                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                      <p className="mt-1 text-sm text-gray-600">
                                        <span className="font-medium text-blue-600 hover:text-blue-500">Click to upload</span> or drag and drop
                                      </p>
                                      <p className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, XLS, XLSX, PNG, JPG up to 10MB</p>
                                    </>
                                  )}
                                </div>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                onChange={handleDocumentUpload}
                                disabled={uploadingDocument}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                              />
                            </label>
                          </div>

                          {/* Documents List */}
                          {taskDocuments.length === 0 ? (
                            <div className="text-center py-8 bg-gray-50 rounded-lg">
                              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              <p className="text-gray-500 text-sm mt-2">No documents uploaded yet</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {taskDocuments.map((doc) => (
                                <div key={doc.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    {/* File Icon */}
                                    <div className="flex-shrink-0">
                                      {doc.file_type?.includes('pdf') ? (
                                        <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M4 18h12V6h-4V2H4v16zm-2 1V0h10l6 6v14H2z"/>
                                        </svg>
                                      ) : doc.file_type?.includes('image') ? (
                                        <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                        </svg>
                                      ) : doc.file_type?.includes('sheet') || doc.file_type?.includes('excel') ? (
                                        <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M4 18h12V6h-4V2H4v16zm-2 1V0h10l6 6v14H2z"/>
                                        </svg>
                                      ) : (
                                        <svg className="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    
                                    {/* File Info */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                                        <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                                        <span>•</span>
                                        <span>{doc.uploaded_by_user?.full_name}</span>
                                        <span>•</span>
                                        <span>{new Date(doc.created_at).toLocaleDateString('id-ID')}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-2 ml-3">
                                    <a
                                      href={doc.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2.5 hover:bg-blue-50 active:bg-blue-100 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                                      title="Download"
                                    >
                                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                    </a>
                                    {doc.uploaded_by === userInfo.id && (
                                      <button
                                        onClick={() => handleDeleteDocument(doc.id, doc.file_path)}
                                        className="p-2.5 hover:bg-red-50 active:bg-red-100 rounded-xl transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
                                        title="Delete"
                                      >
                                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Team Creation Modal */}
      <AnimatePresence>
        {showTeamModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
              onClick={() => setShowTeamModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-xl sm:rounded-t-2xl">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Create New Team</h2>
                  <button
                    onClick={() => setShowTeamModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleCreateTeam} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={teamFormData.name}
                      onChange={(e) => setTeamFormData({ ...teamFormData, name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Engineering Team"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={teamFormData.description}
                      onChange={(e) => setTeamFormData({ ...teamFormData, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Brief description of the team"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Color
                    </label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={teamFormData.color}
                        onChange={(e) => setTeamFormData({ ...teamFormData, color: e.target.value })}
                        className="h-10 w-20 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-600">{teamFormData.color}</span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      type="button"
                      onClick={() => setShowTeamModal(false)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Create Team
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Team Members Management Modal */}
      <AnimatePresence>
        {showMembersModal && managingTeam && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4"
              onClick={() => setShowMembersModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-xl w-full h-[95vh] sm:h-auto sm:max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: managingTeam.color }} />
                      {managingTeam.name}
                    </h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage team members</p>
                  </div>
                  <button
                    onClick={() => setShowMembersModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
                  {/* Current Members */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Team Members ({teamMembers.length})
                    </h3>
                    {teamMembers.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600 text-sm">No members yet</p>
                        <p className="text-gray-500 text-xs mt-1">Add members below to get started</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {teamMembers.map((member) => (
                          <div
                            key={member.user_id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                                {member.full_name?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-gray-900">{member.full_name || 'No name'}</p>
                                  {member.role === 'leader' && (
                                    <span className="text-yellow-500 text-sm">⭐</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">{member.email || 'No email'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleToggleRole(member.user_id, member.role)}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                  member.role === 'leader'
                                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                              >
                                {member.role === 'leader' ? 'Leader' : 'Member'}
                              </button>
                              <button
                                onClick={() => handleRemoveMember(member.user_id)}
                                className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Members */}
                  <div className="border-t pt-6">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Add Members ({availableUsers.length} available)
                    </h3>
                    
                    {availableUsers.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-600 text-sm">No users found</p>
                        <p className="text-gray-500 text-xs mt-1">Make sure users are approved in the system</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {availableUsers
                          .filter(user => !teamMembers.find(m => m.user_id === user.id))
                          .map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center text-white font-semibold">
                                  {user.full_name?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{user.full_name || 'No name'}</p>
                                  <p className="text-xs text-gray-600">{user.email || 'No email'}</p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleAddMember(user.id, 'member')}
                                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                                >
                                  Add as Member
                                </button>
                                <button
                                  onClick={() => handleAddMember(user.id, 'leader')}
                                  className="px-3 py-1.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-xs font-medium"
                                >
                                  Add as Leader
                                </button>
                              </div>
                            </div>
                          ))}
                        {availableUsers.filter(user => !teamMembers.find(m => m.user_id === user.id)).length === 0 && (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            All users are already members of this team
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-lg z-[10000] flex items-center justify-center p-4"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header with red gradient */}
                <div className="bg-gradient-to-r from-red-500 to-pink-600 px-6 py-8 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                    className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg"
                  >
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white mb-2">Delete Task?</h3>
                  <p className="text-red-100 text-sm">This action cannot be undone</p>
                </div>

                {/* Content */}
                <div className="px-6 py-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-red-900 mb-1">Warning!</h4>
                        <p className="text-sm text-red-700">
                          You are about to permanently delete this task along with all its:
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-red-600">
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                            Comments and discussions
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                            Attachments and documents
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                            Activity history
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>
                            Team assignments
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm text-center mb-6">
                    Are you absolutely sure you want to continue?
                  </p>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-semibold hover:-translate-y-0.5"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDelete}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl hover:from-red-700 hover:to-pink-700 transition-all duration-200 font-semibold shadow-lg shadow-red-500/40 hover:shadow-xl hover:shadow-red-500/50 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Yes, Delete
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <Footer />
      </>
      )}
    </div>
  )
}
